/**
 * RFIResponseSection Component
 * Official response form and display for RFIs
 * BuilderBel Construction Project Management System
 */

import { CheckCircle, Clock, Send } from 'lucide-react';
import React, { useState } from 'react';
import { useToast } from '../../contexts/ToastContext';
import { useRespondToRFIMutation } from '../../services/rfiApi';
import type { RFI } from '../../types/rfi';

interface RFIResponseSectionProps {
  projectId: string;
  rfi: RFI;
  canRespond: boolean;
  onSuccess?: () => void;
}

export const RFIResponseSection: React.FC<RFIResponseSectionProps> = ({
  projectId,
  rfi,
  canRespond,
  onSuccess,
}) => {
  const { success, error: showError } = useToast();

  const [responseContent, setResponseContent] = useState('');
  const [notifyDistribution, setNotifyDistribution] = useState(true);

  const [respondToRFI, { isLoading: isResponding }] = useRespondToRFIMutation();

  // Format date
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Handle submit response
  const handleSubmitResponse = async () => {
    if (!responseContent.trim()) {
      showError('Response content is required');
      return;
    }

    try {
      await respondToRFI({
        projectId,
        rfiId: rfi._id,
        data: {
          content: responseContent.trim(),
          notifyDistribution,
        },
      }).unwrap();

      success('Response submitted successfully');
      setResponseContent('');
      onSuccess?.();
    } catch (err: unknown) {
      const error = err as { data?: { message?: string } };
      showError(error.data?.message || 'Failed to submit response');
    }
  };

  // If there's an official response, show it
  if (rfi.officialResponse) {
    return (
      <div className="space-y-4">
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-green-100 rounded-full">
              <CheckCircle className="h-5 w-5 text-green-600" />
            </div>

            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h4 className="text-sm font-semibold text-gray-900">Official Response</h4>
                  <p className="text-xs text-gray-500">
                    Responded by {rfi.officialResponse.respondedByName}
                  </p>
                </div>
                <span className="text-xs text-gray-400">
                  {formatDate(rfi.officialResponse.respondedAt)}
                </span>
              </div>

              <p className="text-sm text-gray-700 whitespace-pre-wrap">
                {rfi.officialResponse.content}
              </p>

              {/* Attachments */}
              {rfi.officialResponse.attachments && rfi.officialResponse.attachments.length > 0 && (
                <div className="mt-3 pt-3 border-t border-green-200">
                  <p className="text-xs font-medium text-gray-600 mb-2">Attachments</p>
                  <div className="flex flex-wrap gap-2">
                    {rfi.officialResponse.attachments.map((attachment, index) => (
                      <a
                        key={index}
                        href={attachment.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-2 py-1 text-xs bg-white border border-green-200 rounded hover:bg-green-50 transition-colors"
                      >
                        {attachment.name}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Response dates info */}
        {rfi.respondedDate && (
          <div className="flex items-center gap-4 text-xs text-gray-500">
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              Response time:{' '}
              {calculateResponseTime(rfi.submittedDate || rfi.createdAt, rfi.respondedDate)}
            </span>
          </div>
        )}
      </div>
    );
  }

  // If can respond, show response form
  if (canRespond) {
    return (
      <div className="space-y-4">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-yellow-100 rounded-full">
              <Clock className="h-5 w-5 text-yellow-600" />
            </div>

            <div className="flex-1">
              <h4 className="text-sm font-semibold text-gray-900 mb-1">Awaiting Response</h4>
              <p className="text-xs text-gray-500 mb-3">
                This RFI is waiting for an official response. Use the form below to provide your
                response.
              </p>

              <div className="space-y-3">
                <textarea
                  value={responseContent}
                  onChange={e => setResponseContent(e.target.value)}
                  rows={5}
                  placeholder="Enter your official response to this RFI..."
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  disabled={isResponding}
                />

                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={notifyDistribution}
                      onChange={e => setNotifyDistribution(e.target.checked)}
                      disabled={isResponding}
                      className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                    />
                    <span className="text-xs text-gray-500">Notify distribution list</span>
                  </label>

                  <button
                    type="button"
                    onClick={handleSubmitResponse}
                    disabled={!responseContent.trim() || isResponding}
                    className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors flex items-center gap-2"
                  >
                    {isResponding ? (
                      <>
                        <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                        <span>Submitting...</span>
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4" />
                        <span>Submit Response</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Days open indicator */}
        <div className="flex items-center gap-4 text-xs text-gray-500">
          <span className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            Open for {rfi.daysOpen} days
          </span>
          {rfi.isOverdue && (
            <span className="text-red-500 font-medium">{rfi.daysOverdue} days overdue</span>
          )}
        </div>
      </div>
    );
  }

  // If not responded and can't respond (view only)
  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-gray-100 rounded-full">
          <Clock className="h-5 w-5 text-gray-400" />
        </div>
        <div>
          <h4 className="text-sm font-medium text-gray-700">No Response Yet</h4>
          <p className="text-xs text-gray-500">This RFI is awaiting an official response.</p>
        </div>
      </div>
    </div>
  );
};

// Helper function to calculate response time
function calculateResponseTime(startDate: string, endDate: string): string {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffMs = end.getTime() - start.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

  if (diffDays === 0) {
    if (diffHours === 0) return 'Less than an hour';
    return `${diffHours} hour${diffHours !== 1 ? 's' : ''}`;
  }

  if (diffDays === 1) {
    return `1 day${diffHours > 0 ? `, ${diffHours} hour${diffHours !== 1 ? 's' : ''}` : ''}`;
  }

  return `${diffDays} days`;
}

export default RFIResponseSection;
